import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useShallow } from "zustand/react/shallow";

import ExpandableSection from "@/components/job/ExpandableSection";
import { Button, Label, TextInput } from "@/components/ui";
import useCUMechanicalJobStore from "@/store/useCUMechanicalJobStore";

const jointEfficiencySchema = z.object({
  joint_efficiency: z
    .number()
    .min(0.001, "Joint Efficiency cannot be below 0.001")
    .max(1, "Joint Efficiency cannot be greater than 1")
    .nullable(),
  mill_tolerance: z
    .number()
    .min(0, "Mill Tolerance cannot be less than zero")
    .max(100, "Mill Tolerance cannot be greater than 100")
    .nullable(),
  corrosion_allowance: z.number().min(0, "Corrosion Allowance cannot be less than zero").nullable(),
});

type JointEfficiencyFormData = z.infer<typeof jointEfficiencySchema>;

const cardName = "jointEfficiency" as const;

export function JointEfficiency() {
  const { sections, toggleExpandableSection, setJointEfficiency } = useCUMechanicalJobStore(
    useShallow((state) => ({
      sections: state.sections,
      toggleExpandableSection: state.toggleExpandableSection,
      setJointEfficiency: state.setJointEfficiency,
    }))
  );
  const savedJointEfficiency = sections.jointEfficiency;
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<JointEfficiencyFormData>({
    resolver: zodResolver(jointEfficiencySchema),
    mode: "onChange",
    defaultValues: {
      joint_efficiency: null,
      mill_tolerance: null,
      corrosion_allowance: null,
    },
  });

  const joint_efficiency = watch("joint_efficiency");
  const mill_tolerance = watch("mill_tolerance");
  const corrosion_allowance = watch("corrosion_allowance");
  const [hasAtLeastOneValue, setHasAtLeastOneValue] = useState(false);

  // Load saved data
  useEffect(() => {
    if (savedJointEfficiency.isDataFilled && savedJointEfficiency.data) {
      setValue("joint_efficiency", savedJointEfficiency.data.joint_efficiency);
      setValue("mill_tolerance", savedJointEfficiency.data.mill_tolerance);
      setValue("corrosion_allowance", savedJointEfficiency.data.corrosion_allowance);
    }
  }, [savedJointEfficiency, setValue]);

  // Check if at least one field has a value
  useEffect(() => {
    const hasValue = joint_efficiency !== null || mill_tolerance !== null || corrosion_allowance !== null;
    setHasAtLeastOneValue(hasValue);
  }, [joint_efficiency, mill_tolerance, corrosion_allowance]);

  const onSubmit = (data: JointEfficiencyFormData) => {
    setJointEfficiency({
      data,
      status: "done",
    });
  };

  return (
    <ExpandableSection
      setIsExpanded={() => toggleExpandableSection(cardName)}
      status={savedJointEfficiency.status}
      title="Joint Efficiency, Mill Tolerance, and Corrosion Allowance"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="joint_efficiency">Joint Efficiency</Label>
            <TextInput
              id="joint_efficiency"
              type="number"
              step="0.001"
              min="0.001"
              max="1"
              {...register("joint_efficiency", {
                valueAsNumber: true,
                setValueAs: (value) => (value === "" ? null : Number.parseFloat(value.replace(",", "."))),
              })}
            />
            {errors.joint_efficiency && <p className="text-sm text-red-500">{errors.joint_efficiency.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="mill_tolerance">Mill Tolerance</Label>
            <div className="relative">
              <TextInput
                id="mill_tolerance"
                type="number"
                step="0.001"
                min="0"
                max="100"
                {...register("mill_tolerance", {
                  valueAsNumber: true,
                  setValueAs: (value) => (value === "" ? null : Number.parseFloat(value.replace(",", "."))),
                })}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">%</span>
            </div>
            {errors.mill_tolerance && <p className="text-sm text-red-500">{errors.mill_tolerance.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="corrosion_allowance">Corrosion Allowance</Label>
            <div className="relative">
              <TextInput
                id="corrosion_allowance"
                type="number"
                step="0.001"
                min="0"
                {...register("corrosion_allowance", {
                  valueAsNumber: true,
                  setValueAs: (value) => (value === "" ? null : Number.parseFloat(value.replace(",", "."))),
                })}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 font-medium">in</span>
            </div>
            {errors.corrosion_allowance && <p className="text-sm text-red-500">{errors.corrosion_allowance.message}</p>}
          </div>
        </div>

        <Button type="submit" className="self-end" disabled={!hasAtLeastOneValue || !isValid}>
          Save
        </Button>
      </form>
    </ExpandableSection>
  );
}
